// src/utils/analytics.js - FIXED VERSION with all exports
import ReactGA from 'react-ga4';

// Configuration
const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;
const isProduction = import.meta.env.PROD;
const isDevelopment = import.meta.env.DEV;

// Track initialization state
let isInitialized = false;
let initializationPromise = null;

// Initialize Google Analytics with proper error handling
export const initGA = async () => {
  if (isInitialized) {
    return true;
  }

  if (initializationPromise) {
    return initializationPromise;
  }

  initializationPromise = new Promise((resolve) => {
    try {
      if (GA_MEASUREMENT_ID && (isProduction || isDevelopment)) {
        ReactGA.initialize(GA_MEASUREMENT_ID, {
          testMode: isDevelopment,
          gtagOptions: {
            debug_mode: isDevelopment,
            send_page_view: false // We'll manually send page views
          }
        });
        
        console.log('🔍 Google Analytics initialized:', GA_MEASUREMENT_ID);
        isInitialized = true;
        resolve(true);
      } else {
        console.log('🔍 Google Analytics not initialized:', {
          hasMeasurementId: !!GA_MEASUREMENT_ID,
          mode: isDevelopment ? 'development' : 'production'
        });
        resolve(false);
      }
    } catch (error) {
      console.error('❌ Error initializing Google Analytics:', error);
      resolve(false);
    }
  });

  return initializationPromise;
};

// Enhanced ready check
export const isGAReady = () => {
  return isInitialized && GA_MEASUREMENT_ID;
};

// Wait for GA to be ready (useful for critical tracking)
export const waitForGA = async (timeout = 5000) => {
  if (isGAReady()) return true;
  
  const startTime = Date.now();
  while (Date.now() - startTime < timeout) {
    if (isGAReady()) return true;
    await new Promise(resolve => setTimeout(resolve, 100));
  }
  return false;
};

// Enhanced page view tracking with retry logic
export const trackPageView = async (path, title, additionalData = {}) => {
  if (!isGAReady()) {
    console.log('📊 [DEV] Page View:', { path, title, ...additionalData });
    return;
  }

  try {
    ReactGA.send({
      hitType: 'pageview',
      page: path,
      title: title,
      ...additionalData
    });
    
    console.log('📊 Page View Tracked:', { path, title });
  } catch (error) {
    console.error('❌ Error tracking page view:', error);
  }
};

// Enhanced event tracking with automatic retry
export const trackEvent = async (eventName, parameters = {}) => {
  if (!isGAReady()) {
    console.log('📊 [DEV] Event:', eventName, parameters);
    return;
  }

  try {
    ReactGA.event(eventName, parameters);
    console.log('📊 Event Tracked:', eventName, parameters);
  } catch (error) {
    console.error('❌ Error tracking event:', error);
    // Optional: retry logic
    setTimeout(() => {
      try {
        ReactGA.event(eventName, parameters);
      } catch (retryError) {
        console.error('❌ Retry failed for event:', eventName, retryError);
      }
    }, 1000);
  }
};

// Email signup tracking (enhanced for your existing EmailSignup component)
export const trackEmailSignup = async (source, metadata = {}) => {
  const eventData = {
    signup_source: source,
    value: 1,
    currency: 'USD',
    ...metadata
  };

  await trackEvent('email_signup', eventData);
  
  // Also track as a conversion
  await trackEvent('generate_lead', {
    value: 1,
    currency: 'USD',
    source: source
  });
};

// Blog article tracking
export const trackBlogView = async (articleSlug, articleTitle, category, metadata = {}) => {
  await trackEvent('blog_view', {
    article_slug: articleSlug,
    article_title: articleTitle,
    article_category: category,
    content_type: 'blog_article',
    ...metadata
  });
};

export const trackBlogEngagement = async (action, articleSlug, metadata = {}) => {
  await trackEvent('blog_engagement', {
    engagement_type: action, // 'like', 'share', 'bookmark', 'comment'
    article_slug: articleSlug,
    ...metadata
  });
};

// Event tracking (for your events pages)
export const trackEventView = async (eventSlug, eventTitle, eventType, metadata = {}) => {
  await trackEvent('event_view', {
    event_slug: eventSlug,
    event_title: eventTitle,
    event_type: eventType,
    content_type: 'event_detail',
    ...metadata
  });
};

export const trackEventRSVP = async (eventSlug, eventTitle, rsvpType, metadata = {}) => {
  await trackEvent('event_rsvp', {
    event_slug: eventSlug,
    event_title: eventTitle,
    rsvp_type: rsvpType, // 'waitlist', 'reserve', 'cancel'
    value: 1,
    currency: 'USD',
    ...metadata
  });
  
  // Also track as conversion
  await trackEvent('generate_lead', {
    value: 1,
    currency: 'USD',
    source: `event_${rsvpType}`,
    event_title: eventTitle
  });
};

// Navigation tracking
export const trackNavigation = async (from, to, method = 'click') => {
  await trackEvent('navigation', {
    from_page: from,
    to_page: to,
    navigation_method: method
  });
};

// User engagement tracking
export const trackUserEngagement = async (action, details = {}) => {
  await trackEvent('user_engagement', {
    engagement_type: action,
    ...details
  });
};

// Scroll tracking (for blog articles) - FIXED: Now properly exported
export const trackScrollDepth = async (depth, page) => {
  await trackEvent('scroll', {
    percent_scrolled: depth,
    page_location: page
  });
};

// File download tracking (if you add downloadable content)
export const trackDownload = async (fileName, fileType, source = 'unknown') => {
  await trackEvent('file_download', {
    file_name: fileName,
    file_type: fileType,
    download_source: source
  });
};

// Search tracking (if you add search functionality)
export const trackSearch = async (searchTerm, results = 0, source = 'site_search') => {
  await trackEvent('search', {
    search_term: searchTerm,
    search_results: results,
    search_source: source
  });
};

// Exception tracking
export const trackException = async (description, fatal = false) => {
  if (!isGAReady()) {
    console.log('📊 [DEV] Exception:', description, { fatal });
    return;
  }

  try {
    ReactGA.gtag('event', 'exception', {
      description: description,
      fatal: fatal
    });
  } catch (error) {
    console.error('❌ Error tracking exception:', error);
  }
};

// Timing tracking (for performance monitoring)
export const trackTiming = async (category, variable, value, label = '') => {
  if (!isGAReady()) return;

  try {
    ReactGA.gtag('event', 'timing_complete', {
      name: variable,
      value: value,
      event_category: category,
      event_label: label
    });
  } catch (error) {
    console.error('❌ Error tracking timing:', error);
  }
};

// Helper to track component mount times (useful for performance)
export const useComponentTiming = (componentName) => {
  const startTime = performance.now();
  
  return () => {
    const endTime = performance.now();
    const duration = Math.round(endTime - startTime);
    trackTiming('component_performance', componentName, duration);
  };
};

// FIXED: Export all functions properly
export default {
  initGA,
  isGAReady,
  waitForGA,
  trackPageView,
  trackEvent,
  trackEmailSignup,
  trackBlogView,
  trackBlogEngagement,
  trackEventView,
  trackEventRSVP,
  trackNavigation,
  trackUserEngagement,
  trackScrollDepth, // ← NOW PROPERLY EXPORTED
  trackDownload,
  trackSearch,
  trackException,
  trackTiming,
  useComponentTiming
};